import { Component, computed, inject, signal, viewChild, OnInit, PLATFORM_ID } from '@angular/core';
import { brandAlpha } from '../../services/theme';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import {
  PartnerDashboardService,
  DashboardFilter,
  DashboardOverview,
  currentFinYear,
} from '../../services/partner-dashboard';
import { PartnerService } from '../../services/partner';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';
import { PartnerFilterBar } from './partner-filter-bar';
import { CollectPaymentFlow, CollectTarget } from './collect-payment-flow';
import { PAY, KYC, Severity } from './status-severity';
import { UnissuedPanel } from '../../components/unissued-panel/unissued-panel';
import { TestModeService } from '../../services/test-mode';

@Component({
  selector: 'app-partner-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ChartModule,
    TableModule,
    TagModule,
    ButtonModule,
    SkeletonModule,
    TooltipModule,
    IndDatePipe,
    PartnerFilterBar,
    CollectPaymentFlow,
    UnissuedPanel,
  ],
  templateUrl: './partner-dashboard.html',
  styleUrls: ['./partner-dashboard.css'],
})
export class PartnerDashboard implements OnInit {
  private svc = inject(PartnerDashboardService);
  private partnerSvc = inject(PartnerService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  /** The inline collect-payment flow (opens the assigned gateway in place where it can). */
  private flow = viewChild(CollectPaymentFlow);
  private filterBar = viewChild(PartnerFilterBar);

  /** Drives the "you are looking at test records" badge — pinned server-side for partners. */
  readonly testMode = inject(TestModeService);

  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly isAdmin = this.auth.isAdmin();

  /** Shared period + partner filter currently in effect (set by the filter bar). */
  private applied = signal<DashboardFilter>({ finYear: currentFinYear() });

  partnerOptions = signal<{ label: string; value: number }[]>([]);

  overviewLoading = signal(false);
  overview = signal<DashboardOverview | null>(null);

  // ===================== chart styling (mirrors dashboard.ts) =====================
  trendOptions: any = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { labels: { color: '#475569', usePointStyle: true } } },
    scales: {
      x: { ticks: { color: '#64748b' }, grid: { color: '#eef2f6' } },
      y: {
        position: 'left',
        ticks: { color: '#64748b' },
        grid: { color: '#eef2f6' },
        title: { display: true, text: 'Amount (₹)', color: '#64748b' },
      },
      y1: {
        position: 'right',
        ticks: { color: '#64748b', precision: 0 },
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Members', color: '#64748b' },
      },
    },
  };

  doughnutOptions: any = {
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: { legend: { position: 'bottom', labels: { color: '#475569', usePointStyle: true } } },
  };

  // ===================== derived view data =====================
  scope = computed(() => this.overview()?.scope ?? null);
  period = computed(() => this.overview()?.period ?? null);
  recentMembers = computed(() => this.overview()?.recentMembers ?? []);

  partnerLabel = computed(() => {
    const s = this.scope();
    if (s?.partnerName) return s.partnerName;
    return this.isAdmin ? 'All Partners' : '—';
  });

  summary = computed(() => this.overview()?.summary ?? null);
  paymentStats = computed(() => this.overview()?.paymentStats ?? null);

  /** When the figures on screen were last fetched — so a stale tab admits it. */
  readonly lastLoaded = signal<Date | null>(null);

  // ---------- derived KPIs (all fall out of data the overview already returns) ----------
  // Every rate goes through this: a period with nothing in it is 0%, never NaN%.
  private static pct(part: number, whole: number): number {
    return whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;
  }

  /** Share of billed premium actually banked. */
  readonly collectionRate = computed(() =>
    PartnerDashboard.pct(Number(this.summary()?.collectedAmount ?? 0), Number(this.summary()?.totalPremium ?? 0)),
  );

  /** Gateway health: successful attempts as a share of all attempts. */
  readonly successRate = computed(() =>
    PartnerDashboard.pct(Number(this.paymentStats()?.successCount ?? 0), Number(this.paymentStats()?.totalTransactions ?? 0)),
  );

  readonly kycRate = computed(() =>
    PartnerDashboard.pct(Number(this.summary()?.kycVerified ?? 0), Number(this.summary()?.totalMembers ?? 0)),
  );

  readonly paidRate = computed(() =>
    PartnerDashboard.pct(Number(this.summary()?.paidMembers ?? 0), Number(this.summary()?.totalMembers ?? 0)),
  );

  /** Average premium per enrolled member — whole rupees, like every other amount here. */
  readonly avgPremium = computed(() => {
    const s = this.summary();
    const n = Number(s?.totalMembers ?? 0);
    return n > 0 ? Math.round(Number(s?.totalPremium ?? 0) / n) : 0;
  });

  /**
   * Transaction mix as plain percentages, drawn as a bar.
   *
   * Three slices do not need a 300px doughnut to be understood, and the bar sits level with the
   * list beside it instead of leaving the card half empty.
   */
  readonly txMix = computed(() => {
    const p = this.paymentStats();
    const ok = Number(p?.successCount ?? 0);
    const bad = Number(p?.failedCount ?? 0);
    const wait = Number(p?.pendingCount ?? 0);
    const total = ok + bad + wait;
    return {
      total, ok, bad, wait,
      okPct: PartnerDashboard.pct(ok, total),
      badPct: PartnerDashboard.pct(bad, total),
      waitPct: PartnerDashboard.pct(wait, total),
    };
  });

  /** Nothing at all in this period — worth explaining rather than showing a wall of zeros. */
  isEmpty = computed(() => !this.overviewLoading() && (this.summary()?.totalMembers ?? 0) === 0);

  /** Monthly-trend combo chart: bars = members (right axis), lines = collected/pending (left axis). */
  trendData = computed(() => {
    const t = this.overview()?.monthlyTrend ?? [];
    return {
      labels: t.map((p) => p.label),
      datasets: [
        {
          type: 'line',
          label: 'Collected ₹',
          data: t.map((p) => p.collected),
          yAxisID: 'y',
          borderColor: '#16a34a',
          backgroundColor: 'rgba(22, 163, 74, 0.12)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#16a34a',
        },
        {
          type: 'line',
          label: 'Pending ₹',
          data: t.map((p) => p.pending),
          yAxisID: 'y',
          borderColor: '#d97706',
          backgroundColor: 'rgba(217, 119, 6, 0.12)',
          tension: 0.4,
          fill: false,
          pointBackgroundColor: '#d97706',
        },
        {
          type: 'bar',
          label: 'Members',
          data: t.map((p) => p.members),
          yAxisID: 'y1',
          backgroundColor: brandAlpha(0.45),
          borderRadius: 6,
        },
      ],
    };
  });

  hasTrend = computed(() => (this.overview()?.monthlyTrend?.length ?? 0) > 0);

  mixData = computed(() => {
    const p = this.overview()?.paymentStats;
    return {
      labels: ['Success', 'Failed', 'Pending'],
      datasets: [
        {
          data: [p?.successCount ?? 0, p?.failedCount ?? 0, p?.pendingCount ?? 0],
          backgroundColor: ['#16a34a', '#dc3545', '#d97706'],
          hoverBackgroundColor: ['#15803d', '#b91c1c', '#b45309'],
        },
      ],
    };
  });

  hasMix = computed(() => {
    const p = this.overview()?.paymentStats;
    return (p?.successCount ?? 0) + (p?.failedCount ?? 0) + (p?.pendingCount ?? 0) > 0;
  });

  // ===================== lifecycle =====================
  ngOnInit() {
    if (!this.isBrowser) return;
    if (this.isAdmin) this.loadPartners();
    this.loadOverview();
  }

  /** The filter bar emitted a new period/partner selection (Apply / Reset). */
  onFilter(f: DashboardFilter) {
    this.applied.set(f);
    this.loadOverview();
  }

  private loadPartners() {
    this.partnerSvc.getAll().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? [];
        const list = Array.isArray(data) ? data : [];
        this.partnerOptions.set(
          list.map((p: any) => ({
            label: p.partnerName ?? p.partnerCode ?? `Partner #${p.id}`,
            value: p.id,
          })),
        );
      },
      error: () => {},
    });
  }

  loadOverview() {
    if (!this.isBrowser) return;
    this.overviewLoading.set(true);
    this.svc.overview(this.applied()).subscribe({
      next: (res) => {
        this.overviewLoading.set(false);
        if (res?.success) {
          this.overview.set(res.data);
          this.lastLoaded.set(new Date());
        } else {
          this.overview.set(null);
          this.toast.error(res?.message || 'Failed to load dashboard overview');
        }
      },
      error: () => {
        this.overviewLoading.set(false);
        this.overview.set(null);
      },
    });
  }

  /** True for a member payment status that still needs collecting. */
  needsPayment(s?: string | null): boolean {
    const v = (s || '').toUpperCase();
    return v === 'UNPAID' || v === 'FAILED' || v === 'CANCELLED' || v === 'PENDING' || v === 'INITIATED';
  }

  /** Collect payment inline — opens Razorpay right here (verified) or OTP-first (unverified). */
  collect(t: CollectTarget) {
    this.flow()?.start(t);
  }

  /** A payment completed in the inline flow — refresh the overview so the cards/list update. */
  onPaid() {
    this.loadOverview();
  }

  /**
   * Widen the period to all time. The commonest reason a partner sees an empty dashboard is that
   * they are looking at one financial year while their work sits in another, so the empty state
   * offers the fix instead of only describing the problem.
   */
  showAllTime() {
    this.filterBar()?.reset();
  }

  /** A member with a policy number has a certificate to show. */
  hasPolicy(r: any): boolean {
    return !!r?.policyNo;
  }

  /** PAID with no policy number — the certificate was never produced. */
  isStranded(r: any): boolean {
    return (r?.paymentStatus || '').toUpperCase() === 'PAID' && !r?.policyNo;
  }

  // ===================== display helpers =====================
  inr(n: number | null | undefined): string {
    return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  /** Compact form for a caption, where "₹12,45,000" would crowd out the words around it. */
  inrShort(v?: number | null): string {
    const n = Math.round(Number(v ?? 0));
    const a = Math.abs(n);
    if (a >= 1e7) return '₹' + (n / 1e7).toFixed(a >= 1e8 ? 0 : 1).replace(/\.0$/, '') + 'Cr';
    if (a >= 1e5) return '₹' + (n / 1e5).toFixed(a >= 1e6 ? 0 : 1).replace(/\.0$/, '') + 'L';
    return '₹' + n.toLocaleString('en-IN');
  }

  num(n: number | null | undefined): string {
    return Number(n ?? 0).toLocaleString('en-IN');
  }

  paySeverity(s?: string | null): Severity {
    return (PAY[(s || '').toUpperCase()] ?? 'secondary') as Severity;
  }
  kycSeverity(s?: string | null): Severity {
    return (KYC[(s || '').toUpperCase()] ?? 'secondary') as Severity;
  }
}
